import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import type { FormNode } from '../../../../types/form.types'
import { isSplitCaptionPreset } from '@/shared/form/labelPreset'
import {
  resolveLabelColumnWidthPx,
  resolveLabelPosition,
  resolveTitleAlign,
  resolveTitleVerticalAlign,
} from '@/shared/form/labelBox'
import { useFormChrome } from '../formChromeTheme'
import { resolveLabel } from '@/runtime/resolveLabel'

function isBooleanType(type?: string): boolean {
  if (!type) return false
  const t = type.toLowerCase()
  return t.includes('boolean') || t === 'bool'
}

function isReferenceType(type?: string): boolean {
  if (!type) return false
  const t = type.toLowerCase()
  return t.includes('ref') || t.includes('reference') || t.includes('catalogref')
}

function isDateType(type?: string): boolean {
  if (!type) return false
  const t = type.toLowerCase()
  return t === 'date' || t === 'datetime' || t === 'timestamp'
}

export interface InputFieldElementProps {
  node: FormNode
  preview?: boolean
  /** Resolved from dataPath + availableAttributes; drives Boolean -> checkbox, Ref -> select */
  attributeType?: string
  /**
   * App / live preview: real control (Controller) — same chrome as canvas/mock (WYSIWYG).
   * When set, mock boolean/ref/text below is skipped.
   */
  slot?: ReactNode
  /**
   * Show the mono type micro-label (date/ref/…).
   * Designer canvas: omit (defaults to !preview → visible).
   * App: pass true only when REACT_APP_DEBUG=true — never on production builds.
   */
  showAttributeType?: boolean
  /**
   * Resolved label-column width. Defaults to `resolveLabelColumnWidthPx`
   * with `autoPx = 120` (today's hardcoded fallback). When `titleWidth` is
   * `auto`, the rendered width is `var(--form-label-col, <this>px)`.
   */
  labelColumnWidthPx?: number
  /** App locale — when omitted, falls back to i18n.language. */
  locale?: string | null
  defaultLocale?: string
}

export function InputFieldElement({
  node,
  preview,
  attributeType,
  slot,
  showAttributeType,
  labelColumnWidthPx,
  locale,
  defaultLocale,
}: InputFieldElementProps) {
  const chrome = useFormChrome()
  const { i18n } = useTranslation()
  const position = resolveLabelPosition(node)
  const titleAlign = resolveTitleAlign(node)
  const titleVAlign = resolveTitleVerticalAlign(node)
  const colPx = labelColumnWidthPx ?? resolveLabelColumnWidthPx(undefined, node.titleWidth, 120)
  const sideLabelWidth =
    typeof node.titleWidth === 'number'
      ? node.titleWidth
      : labelColumnWidthPx != null
        ? labelColumnWidthPx
        : `var(--form-label-col, ${colPx}px)`
  const isBoolean = isBooleanType(attributeType)
  const isRef = isReferenceType(attributeType)
  const isDate = isDateType(attributeType)
  const fieldName = node.dataPath?.replace('Object.', '') ?? node.name
  const s = node.style ?? {}
  const fontSize = s.fontSize ?? 13
  const placeholder = node.hint || fieldName
  const splitCaption = isSplitCaptionPreset(s.labelPreset)
  const labelTop = position === 'top'
  const labelRight = position === 'right'
  const labelNone = position === 'none'
  const prefix = node.inputPrefix?.trim() ? node.inputPrefix : undefined
  const title = resolveLabel(
    { synonyms: node.synonyms, synonym: node.synonym, title: node.title, name: node.name },
    locale ?? i18n?.language ?? 'en',
    defaultLocale,
  )
  const noneAria = labelNone ? { 'aria-label': title } : undefined
  const labelTextAlign: 'left' | 'center' | 'right' =
    titleAlign === 'end' ? 'right' : titleAlign === 'center' ? 'center' : 'left'
  const labelAlignSelf: 'flex-start' | 'center' | 'flex-end' =
    titleVAlign === 'end' ? 'flex-end' : titleVAlign === 'start' ? 'flex-start' : 'center'

  const showTypeChip =
    showAttributeType !== undefined ? showAttributeType : !preview


  const inputSx = {
    fontSize: splitCaption ? 13 : fontSize,
    fontWeight: s.bold ? 700 : undefined,
    fontStyle: s.italic ? ('italic' as const) : undefined,
    color: s.textColor ?? undefined,
  }

  const splitControlChrome = splitCaption
    ? {
        '& .MuiOutlinedInput-root': {
          bgcolor: chrome.inputBg,
          borderRadius: prefix ? '0 8px 8px 0' : '8px',
          fontFamily: chrome.uiFont,
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: chrome.outline },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: chrome.outlineHover },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: chrome.focus },
      }
    : {}

  const labelSx = splitCaption
    ? {
        flexShrink: 0,
        width: labelTop ? '100%' : sideLabelWidth,
        fontFamily: chrome.monoFont,
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
        color: chrome.muted,
        lineHeight: 1.35,
        textAlign: labelTextAlign,
        alignSelf: labelTop ? 'flex-start' : labelAlignSelf,
        pt: labelTop ? 0 : 0.75,
        mb: labelTop ? 0.625 : 0,
      }
    : {
        flexShrink: 0,
        width: labelTop ? '100%' : sideLabelWidth,
        color: s.textColor ?? 'text.secondary',
        fontSize,
        textAlign: labelTextAlign,
        alignSelf: labelTop ? 'stretch' : labelAlignSelf,
        mb: labelTop ? 0.5 : 0,
      }

  const suffix = node.inputSuffix?.trim() ? node.inputSuffix : undefined

  const labelTypography = node.titleVisible && !labelNone && (
    <Typography variant="body2" sx={labelSx}>
      {title}
      {node.required && <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Typography>}
    </Typography>
  )

  // Designer canvas: type chip via !preview. App: only when caller opts in (staging/dev).
  const typeLabel = showTypeChip && attributeType ? (
    <Typography
      component="span"
      title={attributeType}
      data-testid="attr-type-label"
      sx={{
        fontFamily: chrome.monoFont,
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: chrome.muted,
        opacity: 0.75,
        ml: 0.75,
        flexShrink: 0,
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {attributeType}
    </Typography>
  ) : null

  const prefixChrome =
    splitCaption && prefix ? (
      <Box
        {...noneAria}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0,
          ...splitControlChrome,
          '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 1.25,
            py: 1,
            bgcolor: chrome.prefixBg,
            border: `1px solid ${chrome.outline}`,
            borderRight: 'none',
            borderRadius: '8px 0 0 8px',
            fontFamily: chrome.monoFont,
            fontSize: 12,
            color: chrome.muted,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {prefix}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': inputSx }}>{slot}</Box>
      </Box>
    ) : null

  const controlWrapSx = {
    flex: 1,
    minWidth: 0,
    ...splitControlChrome,
    '& .MuiInputBase-input': inputSx,
  }

  if (slot != null) {
    const hintBelow =
      node.hint &&
      (typeof node.hint === 'string' ? node.hint.trim() : '') ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.25,
            ml:
              labelTop || labelNone || labelRight || !node.titleVisible
                ? 0
                : typeof sideLabelWidth === 'number'
                  ? `${sideLabelWidth + 8}px`
                  : `calc(var(--form-label-col, ${colPx}px) + 8px)`,
            lineHeight: 1.35,
            color: splitCaption ? chrome.muted : undefined,
          }}
        >
          {node.hint}
        </Typography>
      ) : null

    const slotResult = labelTop ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
          bgcolor: s.bgColor ?? undefined,
          borderRadius: 0.5,
        }}
      >
        {labelTypography}
        {typeLabel}
        {prefixChrome ?? <Box sx={controlWrapSx} {...noneAria}>{slot}</Box>}
        {hintBelow}
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
          bgcolor: s.bgColor ?? undefined,
          borderRadius: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 36,
          }}
        >
          {labelRight ? (
            <>
              {typeLabel}
              {prefixChrome ?? <Box sx={controlWrapSx} {...noneAria}>{slot}</Box>}
              {labelTypography}
            </>
          ) : (
            <>
              {labelTypography}
              {typeLabel}
              {prefixChrome ?? <Box sx={controlWrapSx} {...noneAria}>{slot}</Box>}
            </>
          )}
        </Box>
        {hintBelow}
      </Box>
    )

    if (node.tooltip) {
      return <Tooltip title={node.tooltip}>{slotResult}</Tooltip>
    }
    return slotResult
  }

  const mockPlaceholder = node.placeholder || placeholder
  const mockMultiline = !!node.multiline
  const mockRows = node.rows ?? 3

  const mockTextField = (
    <TextField
      size="small"
      placeholder={mockPlaceholder}
      fullWidth
      tabIndex={-1}
      type={isDate ? 'date' : 'text'}
      multiline={mockMultiline}
      rows={mockMultiline ? mockRows : undefined}
      helperText={node.hint || undefined}
      InputProps={{
        readOnly: true,
        startAdornment:
          prefix && !splitCaption ? (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ) : undefined,
        endAdornment:
          suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined,
      }}
      inputProps={noneAria}
      InputLabelProps={isDate ? { shrink: true } : undefined}
      sx={{
        minWidth: 0,
        flex: 1,
        pointerEvents: 'none',
        '& .MuiInputBase-input': inputSx,
      }}
    />
  )

  const mockBody = isBoolean ? (
    <FormControlLabel
      control={<Checkbox size="small" checked={false} tabIndex={-1} inputProps={noneAria} sx={{ pointerEvents: 'none' }} />}
      label=""
      sx={{ ml: 0 }}
    />
  ) : isRef ? (
    <FormControl size="small" fullWidth sx={{ minWidth: 0, flex: 1 }}>
      <Select value="" displayEmpty tabIndex={-1} inputProps={noneAria} sx={{ pointerEvents: 'none', ...inputSx }}>
        <MenuItem value="" disabled>
          <em>{placeholder}</em>
        </MenuItem>
      </Select>
    </FormControl>
  ) : splitCaption && prefix ? (
    <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', ...splitControlChrome }}>
      <Box
        sx={{
          flexShrink: 0,
          px: 1.25,
          display: 'flex',
          alignItems: 'center',
          bgcolor: chrome.prefixBg,
          border: `1px solid ${chrome.outline}`,
          borderRight: 'none',
          borderRadius: '8px 0 0 8px',
          fontFamily: chrome.monoFont,
          fontSize: 12,
          color: chrome.muted,
        }}
      >
        {prefix}
      </Box>
      <TextField
        size="small"
        placeholder={placeholder}
        fullWidth
        tabIndex={-1}
        type={isDate ? 'date' : 'text'}
        helperText={node.hint || undefined}
        inputProps={noneAria}
        InputProps={{ readOnly: true }}
        InputLabelProps={isDate ? { shrink: true } : undefined}
        sx={{
          minWidth: 0,
          flex: 1,
          pointerEvents: 'none',
          '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
          '& .MuiInputBase-input': inputSx,
        }}
      />
    </Box>
  ) : (
    mockTextField
  )

  const mockResult = labelTop ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        bgcolor: s.bgColor ?? undefined,
        borderRadius: 0.5,
        ...(!splitCaption || !prefix ? splitControlChrome : {}),
      }}
    >
      {labelTypography}
      {typeLabel}
      {mockBody}
    </Box>
  ) : labelRight ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 36,
        bgcolor: s.bgColor ?? undefined,
        borderRadius: 0.5,
        ...splitControlChrome,
      }}
    >
      {typeLabel}
      {mockBody}
      {labelTypography}
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 36,
        bgcolor: s.bgColor ?? undefined,
        borderRadius: 0.5,
        ...splitControlChrome,
      }}
    >
      {labelTypography}
      {typeLabel}
      {mockBody}
    </Box>
  )

  if (node.tooltip) {
    return <Tooltip title={node.tooltip}>{mockResult}</Tooltip>
  }
  return mockResult
}
